/**
 * How current a company's receivables are, and whether anybody must act — one
 * definition for every screen (Collection OS Phase 0; docs/product/phase-0/01).
 *
 * The accounting source tells us what is owed. Before anybody acts on a figure
 * they must be able to tell how old it is: a connector that stopped, a
 * TallyPrime that was closed, a sheet nobody synced. This reads only what the
 * server already records — no second clock:
 *
 *   TallyPrime   `last_success_at` is the last time Timely confirmed the book
 *                against TallyPrime: a read applied, or a read found unchanged.
 *                The connector re-sends an unchanged book at least hourly
 *                (apps/connector RECONCILE_EVERY_MS), so a healthy connection
 *                is never more than about an hour old. `last_seen_at` is its
 *                last check-in (every 5 minutes); its last report says whether
 *                TallyPrime and the company were open.
 *   Google Sheet a sheet is synced by hand; the last successful balances run
 *                is how old its figures are.
 *
 * `level` is about the figures; `problem` is about why they are not updating,
 * and `needsAdmin` says whether somebody who manages the data source must do
 * something. Collectors are told how old the figures are and that they are not
 * updating; the technical detail (which computer, which step) is for those who
 * manage the source, and never includes an error text or a credential.
 *
 * Nothing here is "live" or "real-time": the newest a figure can be is the
 * last confirmation, and it is always shown with its age.
 */
import { OFFLINE_AFTER_FAILING_MINUTES, OFFLINE_AFTER_MINUTES, STALE_AFTER_MINUTES } from './receivablesSource.js';

/** The connector re-sends an unchanged book at least this often (apps/connector `RECONCILE_EVERY_MS`). */
export const CONFIRMED_EVERY_MINUTES = 60;
/**
 * A TallyPrime book confirmed within this long is current: the hourly
 * confirmation, one 5-minute check-in, and ten minutes' slack. Older than this
 * the connection is slower than it should be.
 */
export const DELAYED_AFTER_MINUTES = CONFIRMED_EVERY_MINUTES + 15;
/**
 * A sheet is synced by hand. Synced within a day is as current as a sheet gets
 * (the daily reminder email is built on a day's figures); older than a week,
 * the book has missed a full week of payments.
 */
export const SHEET_DELAYED_AFTER_MINUTES = 24 * 60;
export const SHEET_STALE_AFTER_MINUTES = 7 * 24 * 60;

export type FreshnessSource = 'tally' | 'google_sheets';

/** What the server says, and nothing a member should not see (`receivables_freshness()`, 0037). */
export interface FreshnessFacts {
    source: FreshnessSource | null;
    /** An update of the book is running now (a balances sync opened in the last 15 minutes and not finished). */
    syncing?: boolean;
    /**
     * With no source connected: when the book last took figures from any source
     * (the last balances sync that completed). A company that disconnected its
     * source keeps its figures, and they must not read as current.
     */
    bookUpdatedAt?: string | null;
    tally: {
        status: 'pending' | 'active' | 'revoked';
        lastSeenAt: string | null;
        lastSuccessAt: string | null;
        revokedAt: string | null;
        /** From the connector's last report; null when it has not reported. */
        tallyReachable: boolean | null;
        companyLoaded: boolean | null;
        /** Its last report carried an error (the text stays on the server). */
        reportedProblem: boolean;
        /** The server's last attempt to apply a read failed. */
        syncFailing: boolean;
        /** The person who approved the connection can no longer sync. */
        needsApproval: boolean;
        /** A read is held for an owner's review (it would mark much of the book paid). */
        heldForReview: boolean;
    } | null;
    sheet: {
        status: 'ok' | 'error' | 'unconfigured';
        /** The last balances sync that completed. */
        lastSyncAt: string | null;
        /** A balances sync that failed after the last one that completed. */
        lastFailedAt: string | null;
    } | null;
}

/** How old the figures are: quiet, slower than usual, old, being set up, or no source at all. */
export type FreshnessLevel = 'current' | 'delayed' | 'stale' | 'setup' | 'none';

export type FreshnessProblem =
    /** No check-in within the offline window: the computer is off, offline, or Timely Connector is not running. */
    | 'connector_silent'
    /** The connector checks in, but TallyPrime is not open on that computer. */
    | 'tally_not_running'
    /** TallyPrime is open, but not the connected company. */
    | 'company_not_open'
    /** The connector checks in and reads, but updates are not going through. */
    | 'update_failing'
    /** The person who approved the connection can no longer sync. */
    | 'needs_approval'
    /** A read waits for an owner's review. */
    | 'held_for_review'
    /** The connector was disconnected; the book keeps its last figures. */
    | 'disconnected'
    /** The last sheet sync failed. */
    | 'sheet_sync_failed'
    /** The sheet cannot be read (moved, unshared, access removed). */
    | 'sheet_unreachable'
    /** Nobody has synced the sheet for over a week. */
    | 'sheet_out_of_date'
    /** The book holds figures, but no source is connected to update them. */
    | 'no_source';

export interface ReceivablesFreshness {
    source: FreshnessSource | null;
    level: FreshnessLevel;
    problem: FreshnessProblem | null;
    /** Somebody who manages the data source must act. */
    needsAdmin: boolean;
    /** When the figures were last confirmed against the source; null when never. */
    updatedAt: string | null;
    minutesSinceUpdate: number | null;
    /** TallyPrime: the connector's last check-in. */
    checkedInAt: string | null;
    /** An update is running now; the figures shown are still the last ones confirmed. */
    syncing: boolean;
}

const minutesBetween = (iso: string | null, now: Date): number | null => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? null : Math.max(0, Math.floor((now.getTime() - t) / 60_000));
};

const levelFor = (age: number | null, delayedAfter: number, staleAfter: number): FreshnessLevel =>
    age === null ? 'setup' : age <= delayedAfter ? 'current' : age <= staleAfter ? 'delayed' : 'stale';

export function receivablesFreshness(facts: FreshnessFacts, now: Date): ReceivablesFreshness {
    const result = (r: Omit<ReceivablesFreshness, 'needsAdmin' | 'syncing'>): ReceivablesFreshness => ({
        ...r,
        needsAdmin: r.problem !== null,
        syncing: r.source !== null && facts.syncing === true,
    });
    if (facts.source === 'tally' && facts.tally) {
        const t = facts.tally;
        const age = minutesBetween(t.lastSuccessAt, now);
        const base = { source: 'tally' as const, updatedAt: t.lastSuccessAt, minutesSinceUpdate: age, checkedInAt: t.lastSeenAt };
        if (t.status === 'revoked') {
            return result({ ...base, level: age === null ? 'none' : levelFor(age, DELAYED_AFTER_MINUTES, STALE_AFTER_MINUTES), problem: 'disconnected' });
        }
        if (t.status === 'pending') return result({ ...base, level: 'setup', problem: null });

        const level = levelFor(age, DELAYED_AFTER_MINUTES, STALE_AFTER_MINUTES);
        // An owner's decision blocks updates whatever the connector does.
        if (t.heldForReview) return result({ ...base, level, problem: 'held_for_review' });
        if (t.needsApproval) return result({ ...base, level, problem: 'needs_approval' });

        // The connector reports at check-in, before that run's update, and its report repeats the previous run's
        // error (apps/connector syncOnce). An update confirmed after the report supersedes the problem it carried:
        // otherwise the first five minutes after TallyPrime reopens would say updates are failing.
        const superseded = t.lastSuccessAt !== null && t.lastSeenAt !== null
            && new Date(t.lastSuccessAt).getTime() >= new Date(t.lastSeenAt).getTime();
        const reportedProblem = t.reportedProblem && !superseded;
        const troubled = t.tallyReachable === false || t.companyLoaded === false || reportedProblem || t.syncFailing;
        const silent = minutesBetween(t.lastSeenAt, now);
        if (silent === null || silent > (troubled ? OFFLINE_AFTER_FAILING_MINUTES : OFFLINE_AFTER_MINUTES)) {
            return result({ ...base, level, problem: 'connector_silent' });
        }
        if (t.tallyReachable === false) return result({ ...base, level, problem: 'tally_not_running' });
        // Before the first update the connector may not yet know the company it was given: not a problem yet.
        if (t.companyLoaded === false && t.lastSuccessAt !== null) return result({ ...base, level, problem: 'company_not_open' });
        if (t.syncFailing || reportedProblem) return result({ ...base, level, problem: 'update_failing' });
        return result({ ...base, level, problem: null });
    }

    if (facts.source === 'google_sheets' && facts.sheet) {
        const s = facts.sheet;
        const age = minutesBetween(s.lastSyncAt, now);
        const level = levelFor(age, SHEET_DELAYED_AFTER_MINUTES, SHEET_STALE_AFTER_MINUTES);
        const base = { source: 'google_sheets' as const, updatedAt: s.lastSyncAt, minutesSinceUpdate: age, checkedInAt: null };
        if (s.status === 'error') return result({ ...base, level, problem: 'sheet_unreachable' });
        if (s.lastFailedAt) return result({ ...base, level, problem: 'sheet_sync_failed' });
        if (level === 'stale') return result({ ...base, level, problem: 'sheet_out_of_date' });
        return result({ ...base, level, problem: null });
    }

    const kept = facts.bookUpdatedAt ?? null;
    return result({ source: null, level: 'none', problem: kept ? 'no_source' : null, updatedAt: kept, minutesSinceUpdate: minutesBetween(kept, now), checkedInAt: null });
}

/**
 * Reads the server's answer (`receivables_freshness()`), keeping only the
 * fields this module knows. Anything malformed reads as "no source" rather
 * than as a confident state.
 */
export function readFreshnessFacts(value: unknown): FreshnessFacts {
    const o = (value ?? {}) as Record<string, unknown>;
    const iso = (v: unknown): string | null => (typeof v === 'string' && !Number.isNaN(new Date(v).getTime()) ? v : null);
    const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
    const source = o['source'] === 'tally' || o['source'] === 'google_sheets' ? o['source'] : null;
    const t = o['tally'] as Record<string, unknown> | null | undefined;
    const s = o['sheet'] as Record<string, unknown> | null | undefined;
    const tStatus = t?.['status'];
    const sStatus = s?.['status'];
    return {
        source,
        syncing: o['syncing'] === true,
        bookUpdatedAt: iso(o['bookUpdatedAt']),
        tally: t && (tStatus === 'pending' || tStatus === 'active' || tStatus === 'revoked') ? {
            status: tStatus,
            lastSeenAt: iso(t['lastSeenAt']),
            lastSuccessAt: iso(t['lastSuccessAt']),
            revokedAt: iso(t['revokedAt']),
            tallyReachable: bool(t['tallyReachable']),
            companyLoaded: bool(t['companyLoaded']),
            reportedProblem: t['reportedProblem'] === true,
            syncFailing: t['syncFailing'] === true,
            needsApproval: t['needsApproval'] === true,
            heldForReview: t['heldForReview'] === true,
        } : null,
        sheet: s && (sStatus === 'ok' || sStatus === 'error' || sStatus === 'unconfigured') ? {
            status: sStatus,
            lastSyncAt: iso(s['lastSyncAt']),
            lastFailedAt: iso(s['lastFailedAt']),
        } : null,
    };
}

/**
 * The age of a figure in words, in the company's time zone:
 * "just now", "3 minutes ago", "2 hours ago", "today at 9:05 am",
 * "yesterday at 4:12 pm", "on 12 Oct 2026 at 4:12 pm".
 *
 * Under six hours a relative age reads best; beyond that the clock time is
 * what people check against ("was that before or after the payment came in?").
 */
export function describeAge(
    iso: string | null, now: Date,
    fmt: { day(value: Date | string): string; time(value: Date | string): string; dayKey(value: Date | string): string },
): string | null {
    const minutes = minutesBetween(iso, now);
    if (iso === null || minutes === null) return null;
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 6) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const then = new Date(iso);
    const yesterday = new Date(now.getTime() - 86_400_000);
    if (fmt.dayKey(then) === fmt.dayKey(now)) return `today at ${fmt.time(then)}`;
    if (fmt.dayKey(then) === fmt.dayKey(yesterday)) return `yesterday at ${fmt.time(then)}`;
    return `on ${fmt.day(then)} at ${fmt.time(then)}`;
}
